<template>
  <template v-if="!loading">
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="8" width-xs="12">
        <vl-title tag-name="h2"> {{ $t('productEntityDataMapping.detail.title') }} </vl-title>
      </vl-column>
      <vl-column v-if="editStore.canEdit" width="4" width-xs="12">
        <vl-action-group mod-align-right>
          <vl-button
            icon="plus"
            mod-icon-before
            :mod-disabled="editStore.isCreate(ProductProcessDefinitionEditType.ProductEntityDataMapping)"
            @click="editStore.openCreate(ProductProcessDefinitionEditType.ProductEntityDataMapping)"
            >{{ $t('general.add') }}</vl-button
          >
          <PbsActionButton
            class="product-entity-data-actions"
            :label="$t('general.moreActions')"
            :items="actionButtonItems"
          >
          </PbsActionButton>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid :class="{ edit: editStore.isCreate(ProductProcessDefinitionEditType.ProductEntityDataMapping) }">
      <vl-column>
        <ProductEntityDataMappingCreate
          v-if="editStore.isCreate(ProductProcessDefinitionEditType.ProductEntityDataMapping)"
          :definition-id="definitionId"
          :parent-definition-id="parentDefinitionId"
        />
      </vl-column>
    </vl-grid>
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="12" width-xs="12">
        <vl-properties-list>
          <vl-properties-label>{{ $t('productEntityDataMapping.detail.sourceDefinition') }}</vl-properties-label>
          <vl-properties-data>
            <PbsEntityRouterLink type="productProcessDefinition" :detail="props.detail.parent" />
          </vl-properties-data>
          <vl-properties-label>{{ $t('productEntityDataMapping.detail.targetDefinition') }}</vl-properties-label>
          <vl-properties-data>
            <PbsEntityRouterLink type="productProcessDefinition" :detail="props.detail" />
          </vl-properties-data>
        </vl-properties-list>
      </vl-column>
    </vl-grid>
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="12" width-xs="12">
        <vl-accordion-list v-if="items && items.length">
          <vl-accordion-list-item v-for="(mapping, i) in items" :id="i" :key="i">
            <vl-accordion :id="i.toString()" :title="getLabel(mapping)">
              <vl-grid
                :class="{
                  edit: editStore.isEdit(ProductProcessDefinitionEditType.ProductEntityDataMapping, mapping.id)
                }"
              >
                <vl-column width="8">
                  <ProductEntityDataMappingEdit
                    v-if="editStore.isEdit(ProductProcessDefinitionEditType.ProductEntityDataMapping, mapping.id)"
                    ref="editForm"
                    :definition-id="definitionId"
                    :parent-definition-id="parentDefinitionId"
                    :detail="mapping"
                  />
                  <ProductEntityDataMappingRead v-else :detail="mapping" />
                </vl-column>
                <vl-column v-if="editStore.canEdit" width="4">
                  <vl-action-group
                    v-if="
                      mapping &&
                      !editStore.isEdit(ProductProcessDefinitionEditType.ProductEntityDataMapping, mapping.id)
                    "
                    mod-align-right
                    mod-space
                    between
                  >
                    <vl-button
                      icon="pencil"
                      mod-icon-before
                      mod-secondary
                      :mod-disabled="editStore.editing"
                      @click="editStore.openEdit(ProductProcessDefinitionEditType.ProductEntityDataMapping, mapping.id)"
                      >{{ $t('general.edit') }}</vl-button
                    >
                    <vl-button icon="trash" mod-icon-before mod-error @click="showDeleteModal(mapping.id)">{{
                      $t('general.delete')
                    }}</vl-button>
                  </vl-action-group>
                  <vl-action-group v-else mod-align-right mod-space between>
                    <vl-button icon="save" x mod-icon-before :mod-loading="reloading" @click="save">{{
                      $t('general.save')
                    }}</vl-button>
                    <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeEdit">
                      {{ $t('general.cancel') }}
                    </vl-button>
                  </vl-action-group>
                </vl-column>
              </vl-grid>
            </vl-accordion>
          </vl-accordion-list-item>
        </vl-accordion-list>
        <template v-else>
          <vl-annotation>{{ $t('productEntityDataMapping.detail.empty') }}</vl-annotation>
        </template>
      </vl-column>
    </vl-grid>
  </template>
</template>
<script lang="ts" setup>
import ProductEntityDataMappingRead from './ProductEntityDataMappingRead.vue';
import ProductEntityDataMappingCreate from './ProductEntityDataMappingCreate.vue';
import ProductEntityDataMappingEdit from './ProductEntityDataMappingEdit.vue';
import { ProductProcessDefinitionDetailDto, ProductEntityDataMappingDetailDto } from '@/api/backoffice/clients';
import { useProductEntityDataMappingStore } from '@/modules/configuration/store/product-entity-data-mapping/product-entity-data-mapping.store';
import {
  ProductProcessDefinitionEditType,
  useProductProcessDefinitionEditStore
} from '@/modules/configuration/store/product-process-definition/product-process-definition-edit.store';
import { computed, onMounted, ref, Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useModalStore } from '@/modules/core/store/modal.store';
import PbsEntityRouterLink from '@/components/PbsEntityRouterLink.vue';
import PbsActionButton, { PbsActionButtonItem } from '@/components/PbsActionButton.vue';

const { t } = useI18n();

const store = useProductEntityDataMappingStore();
const editStore = useProductProcessDefinitionEditStore();
const modalStore = useModalStore();

const editForm: Ref<typeof ProductEntityDataMappingEdit | (typeof ProductEntityDataMappingEdit)[]> = ref(null);

const route = useRoute();

const props = defineProps<{
  detail: ProductProcessDefinitionDetailDto;
}>();

const loading = computed((): boolean => {
  return store.loading;
});

const items = computed((): ProductEntityDataMappingDetailDto[] => {
  return store.byDefinition(definitionId.value);
});

const definitionId = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

const parentDefinitionId = computed((): number => {
  return props.detail.parent?.id;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const actionButtonItems = computed((): PbsActionButtonItem[] => {
  return [
    {
      label: t('productEntityDataMapping.actions.generate.label'),
      click: generateMapping
    },
    {
      label: t('general.delete'),
      click: () => showResetModal(),
      disabled: !editStore.canEdit || !items.value.length,
      ellipsis: true
    }
  ];
});

onMounted(async () => {
  await store.getByDefinition(definitionId.value);
});

const generateMapping = async () => {
  await store.generate(definitionId.value);
};

const save = () => {
  if (Array.isArray(editForm?.value)) {
    editForm.value[0].onSubmit();
  } else {
    editForm.value.onSubmit();
  }
};

const showResetModal = async () => {
  modalStore.showDeleteModal({
    title: t('productEntityDataMapping.detail.reset.title'),
    props: {
      confirm: () => store.reset(definitionId.value)
    }
  });
};

const showDeleteModal = (mappingId: number) => {
  modalStore.showDeleteModal({
    title: t('productEntityDataMapping.detail.delete.title'),
    props: {
      confirm: () => store.remove(definitionId.value, mappingId)
    }
  });
};

const getLabel = (item: ProductEntityDataMappingDetailDto): string => {
  const unknown = '(?)';
  const from = `${item.fromEntity?.code ?? unknown}`;
  const to = `${item.toEntity?.code ?? unknown}`;

  return t('productEntityDataMapping.detail.linkTo', { from, to });
};
</script>
<style lang="scss" scoped>
:deep(.vl-accordion__panel) {
  padding-right: 0;
}
</style>
